import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { CurrentUserService } from '@services/auth/current-user.service';
import { UserService } from '@services/user/user.service';
import { firstValueFrom } from 'rxjs';

@Injectable({
	providedIn: 'root',
})
export class LanguageDetectionService {
	private readonly supportedLanguages = ['es', 'en'];
	private readonly defaultLanguage = 'es';
	private transloco = inject(TranslocoService);
	private currentUserService = inject(CurrentUserService);
	private userService = inject(UserService);

	async detectAndSetLanguage(): Promise<void> {
		// Try to load user's preferred language if authenticated
		const userId = this.currentUserService.getUserId();
		let langToSet = this.defaultLanguage;

		if (userId) {
			// La respuesta del login YA trae `preferredLanguage` y se guarda en
			// sesión: ir a por él a la API era un GET extra por cada login, en los
			// tres flujos (normal, dev y MFA).
			const stored = this.currentUserService.getPreferredLanguage();
			if (stored && this.supportedLanguages.includes(stored)) {
				langToSet = stored;
			} else {
				try {
					// Solo si no está en sesión (cuentas viejas, sesión a medias).
					const user = await firstValueFrom(this.userService.getUserByIdMinimal(String(userId)));
					if (user?.preferredLanguage && this.supportedLanguages.includes(user.preferredLanguage)) {
						langToSet = user.preferredLanguage;
					}
				} catch {
					// Keep default language
				}
			}
		}

		// Set language in next tick to avoid ExpressionChangedAfterItHasBeenCheckedError
		await new Promise<void>((resolve) => {
			setTimeout(() => {
				this.transloco.setActiveLang(langToSet);
				resolve();
			}, 0);
		});

		await this.waitForTranslations();
	}

	/**
	 * Wait for translations to be loaded
	 */
	private async waitForTranslations(): Promise<void> {
		return new Promise((resolve) => {
			// Check if translations are already loaded
			if (this.transloco.getTranslation(this.transloco.getActiveLang())) {
				resolve();
				return;
			}

			// If not loaded, wait for them to load
			const subscription = this.transloco.selectTranslate('nav.inicio').subscribe(() => {
				subscription.unsubscribe();
				resolve();
			});

			// Timeout de seguridad (5 segundos)
			setTimeout(() => {
				subscription.unsubscribe();
				resolve();
			}, 5000);
		});
	}

	/**
	 * Check if translations are loaded
	 */
	areTranslationsLoaded(): boolean {
		return true;
	}

	getCurrentSystemLanguage(): Promise<string> {
		return Promise.resolve(navigator.language);
	}

	/**
	 * Manually change language
	 */
	setLanguage(lang: string): void {
		if (this.supportedLanguages.includes(lang)) {
			this.transloco.setActiveLang(lang);
		}
	}

	/**
	 * Get current active language
	 */
	getCurrentLanguage(): string {
		return this.transloco.getActiveLang();
	}
}
